import { useKeycloak } from "@react-keycloak/web";
import "font-awesome/css/font-awesome.min.css";
import React, { Fragment, useEffect, useState } from "react";
import { VerticalTimeline } from "react-vertical-timeline-component";
import { CardBody, Col, Row, Table } from "reactstrap";
import { CourseDto } from "../../../../models/courseDto";
import { CoursesListDisplayType } from "../../../../models/coursesListDisplayType";
import hasRoles from "../../../../security/hasRoles";
import { useHttpClient } from "../../../../security/hooks/axiosProvider";
import { getStepIcon } from "../../../../shared/timeline-utils/steps-icons";
import styles from "./course.module.scss";
import CourseManager from "./courseManager";
import CoursesTable from "./coursesTable";
import CoursesTimeLine from "./coursesTimeline";
import CourseSuportModal from "./courseSuportModal";
import { translate } from "react-switch-lang";

const CourseList: React.FC<{
  t: any;
  coursesListDisplayType: CoursesListDisplayType;
  moduleId: number;
  isTrainingPathUpdate: boolean;
  disabled?: boolean;
  addCourseToModule?: any;
  courseDtoList?: CourseDto[];
  updateCourseInModuleList?: any;
  deleteCourseInModuleList?: any;
  reloadData?: any;
  refreshCourses?: any;
}> = ({ t, coursesListDisplayType, moduleId, isTrainingPathUpdate, disabled, addCourseToModule, courseDtoList, updateCourseInModuleList, deleteCourseInModuleList, reloadData, refreshCourses }) => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [isUpdateCourse, setIsUpdateCourse] = useState(false);
  const [openModalViewer, setOpenModalViewer] = useState<boolean>(false);
  const { keycloak } = useKeycloak();
  const httpClient = useHttpClient(keycloak.authenticated);
  const hasRoleAdmin = hasRoles(["ROLE_ELEARNING_ADMIN"], keycloak);
  const [course, setCourse] = useState<CourseDto>(null);
  const [modulesPerPage] = useState(6);
  const [, setPageNumbers] = useState(0);
  const [currentPage] = useState(0);
  const [courses, setCourses] = useState<CourseDto[]>([]);
  const [courseToBeUpdated, setCourseToBeUpdated] = useState<CourseDto>(new CourseDto());
  const [isCourseFinished] = useState(false);

  useEffect(() => {
    async function fetchCourses() {
      getCoursesListPerPage();
    }
    fetchCourses();
    // eslint-disable-next-line
  }, []);

  async function getCoursesListPerPage() {
    if (isTrainingPathUpdate) {
      if (coursesListDisplayType === CoursesListDisplayType.TABLE) {
        const responseData = await httpClient.current.get(`/course/ByModule/PerPage/${moduleId}`, { params: { page: currentPage, size: modulesPerPage } });
        setCourses(() => responseData?.data.content);
        setPageNumbers(responseData?.data?.totalPages);
        reloadData();
      } else {
        const responseData = await httpClient.current.get(`/course/ByModule/${moduleId}`);
        setCourses(() => responseData?.data);
      }
    } else {
      setCourses(() => courseDtoList);
    }
  }

  const manageCourse = (courseData: CourseDto) => {
    if (isUpdateCourse) {
      updateCourseInModuleList(moduleId, courseData);
      setCourses(() => courseDtoList);
    } else {
      addCourseToModule(moduleId, courseData);
      setCourses(() => courseDtoList);
    }
  };

  const deleteCourse = async (courseID: number) => {
    if (isTrainingPathUpdate) {
      await httpClient.current.delete(`/course/${courseID}`);
      getCoursesListPerPage();
    } else {
      courses.forEach(course => {
        if (course.id === courseID && course.courseType !== "VIDEO") {
          deleteCourseSupport(course.supportPath);
        }
      });
      deleteCourseInModuleList(courseID, moduleId);
      getCoursesListPerPage();
    }
  };

  const deleteCourseSupport = (path: string) => {
    httpClient.current?.delete(`/course/support`, {
      params: {
        filePath: path,
      },
    });
  };

  const getCourseTimeString = (courseHours: number, courseMinuts: number) => {
    if (courseHours.toString() === "0") {
      return courseMinuts + " min ";
    } else if (courseMinuts.toString() === "0") {
      return courseHours + " h ";
    } else {
      return courseHours + " h " + courseMinuts + " min ";
    }
  };

  const handleCreate = () => {
    setTimeout(() => {
      setCourseToBeUpdated(null);
      setIsUpdateCourse(false);
      setIsModalOpened(true);
    }, 200);
  };

  const handleUpdate = (course: CourseDto) => {
    setCourseToBeUpdated(() => course);
    setIsModalOpened(true);
    setIsUpdateCourse(true);
  };

  const handleClickOpenModalViewer = async (courseDTO: CourseDto) => {
    setCourse(courseDTO);
    setTimeout(async () => {
      if (!hasRoleAdmin) {
        if (openModalViewer) {
          setOpenModalViewer(!openModalViewer);
          if (courseDTO && courseDTO.id) {
            await httpClient.current.get(`/course/isCourseFinished/${courseDTO?.id}`);
            refreshCourses();
          }
          // setIsCourseFinished(response.data);
        } else {
          setOpenModalViewer(!openModalViewer);
        }
        getCoursesListPerPage();
      } else {
        setOpenModalViewer(!openModalViewer);
      }
    }, 400);
  };

  return (
    <>
      <CardBody>
        {hasRoleAdmin && (
          <Row xs="12">
            <Col>
              <h5>{t("list_cours")}</h5>
            </Col>
            {coursesListDisplayType === CoursesListDisplayType.TABLE && !disabled && (
              <Col sm="4" className={"text-right"}>
                <i onClick={handleCreate} title={"Créer un nouveau cours"} id={styles["add_course_button"]} className="fa fa-plus-circle text-success"></i>
              </Col>
            )}
          </Row>
        )}

        {courses.length === 0 ? (
          <p className={styles["empty_courses_message"]}>{t("please_add_new_cours")}</p>
        ) : coursesListDisplayType === CoursesListDisplayType.TABLE ? (
          <>
            <Row className={styles["card-block"]}>
              <Table className="table-styling">
                <thead>
                  <tr>
                    <th>{"Titre du cours"}</th>
                    <th className={styles["center_content"]}>{"Durée du cours"}</th>
                    <th className={styles["center_content"]}>{"Type"}</th>
                    <th className={styles["center_content"]}>{"Actions"}</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map(course => (
                    <CoursesTable
                      key={course.id}
                      course={course}
                      getCourseTimeString={getCourseTimeString}
                      handleClickOpenModalViewer={handleClickOpenModalViewer}
                      handleUpdate={handleUpdate}
                      deleteCourse={deleteCourse}
                      getStepIcon={getStepIcon}
                      disabled={disabled}
                    />
                  ))}
                </tbody>
              </Table>
            </Row>
          </>
        ) : (
          <Fragment>
            <VerticalTimeline contentStyle={{ padding: "0px" }}>
              {courses.map((course, index) => (
                <CoursesTimeLine key={index} course={course} getCourseTimeString={getCourseTimeString} handleClickOpenModalViewer={handleClickOpenModalViewer} getStepIcon={getStepIcon} />
              ))}
            </VerticalTimeline>
          </Fragment>
        )}
        <CourseSuportModal isCourseFinished={isCourseFinished} course={course} openModalViewer={openModalViewer} handleClickOpenModalViewer={handleClickOpenModalViewer} />
        <CourseManager
          key={isUpdateCourse ? courseToBeUpdated?.id : moduleId}
          setIsModalOpened={setIsModalOpened}
          modalState={isModalOpened}
          manageCourse={manageCourse}
          isUpdateTrainingPath={isTrainingPathUpdate}
          isUpdateCourse={isUpdateCourse}
          courseToBeUpdated={courseToBeUpdated}
          setIsUpdateCourse={setIsUpdateCourse}
          getCoursesListPerPage={getCoursesListPerPage}
          setCourseList={setCourses}
          moduleId={moduleId}
          courseLenght={courses.length}
        />
      </CardBody>
    </>
  );
};

export default translate(CourseList);
